<template>
  <div>
    <!-- Floating Chat Button -->
    <div class="floating-chat-button">
      <button
        @click="toggleChat"
        class="stk-button stk-is-primary chat-button"
        :class="{ active: showChat }"
      >
        <i class="fas fa-comments"></i>
        <span class="stk-ml-2">AI Assistant</span>
      </button>
    </div>

    <!-- Chat Panel -->
    <div v-if="showChat" class="chat-panel">
      <div class="chat-header">
        <h3 class="chat-header-title">AI Assistant</h3>
        <div class="stk-buttons stk-gap-2">
          <button @click="toggleChat" class="stk-button is-primary is-icon-only stk-size-3 close-chat-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="chat-body" ref="chatBody">
      <!-- Analysis Controls -->
      <div class="chat-controls stk-p-3 stk-border-b">
        <div class="stk-mb-2">
          <label class="stk-text-xs stk-font-medium stk-text-gray-600">Analysis Depth:</label>
          <select v-model="analysisDepth" class="stk-select stk-w-full stk-mt-1 stk-text-sm">
            <option value="standard">Standard</option>
            <option value="comprehensive">Comprehensive</option>
          </select>
        </div>

        <div>
          <label class="stk-text-xs stk-font-medium stk-text-gray-600">Data Sources:</label>
          <div class="stk-mt-1 stk-flex stk-gap-3">
            <label v-for="source in enabledSources" :key="source.id" class="stk-flex stk-items-center">
              <input
                type="checkbox"
                v-model="source.enabled"
                class="stk-mr-1"
              />
              <span class="stk-text-xs">{{ source.label }}</span>
            </label>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="stk-mt-3">
          <label class="stk-text-xs stk-font-medium stk-text-gray-600">Documents:</label>

          <!-- Loading state -->
          <div v-if="loadingDocuments" class="stk-mt-1 stk-text-xs stk-text-gray-500">
            <i class="fas fa-spinner fa-spin stk-mr-1"></i>
            Loading documents...
          </div>

          <!-- No documents -->
          <div v-else-if="availableDocuments.length === 0" class="stk-mt-1 stk-text-xs stk-text-gray-500">
            No documents available
          </div>

          <!-- Document checkboxes -->
          <div v-else class="stk-mt-1 stk-max-h-24 stk-overflow-y-auto">
            <label
              v-for="doc in availableDocuments"
              :key="doc.external_id"
              class="stk-flex stk-items-center stk-mb-1"
            >
              <input
                type="checkbox"
                v-model="selectedDocumentIds"
                :value="doc.external_id"
                class="stk-mr-1"
              />
              <span class="stk-text-xs stk-truncate" :title="doc.filename">
                {{ truncateFilename(doc.filename) }}
              </span>
            </label>
          </div>

          <!-- Select all/none helpers -->
          <div v-if="availableDocuments.length > 0" class="stk-mt-1 stk-flex stk-gap-2">
            <button
              @click="selectAllDocuments"
              class="stk-text-xs"
              type="button"
            >
              Select All
            </button>
            <button
              @click="selectNoDocuments"
              class="stk-text-xs"
              type="button"
            >
              Select None
            </button>
          </div>
        </div>
      </div>

      <!-- Welcome Message & Suggestions (only when no messages) -->
      <div v-if="messages.length === 0" class="chat-welcome stk-p-3">
        <p class="stk-text-gray-600 stk-mb-3 stk-text-sm">Hi! I'm your AI assistant. I can help you analyze this client's information.</p>

        <!-- Smart Suggestions -->
        <div class="suggestion-chips">
          <button
            v-for="suggestion in chatSuggestions"
            :key="suggestion"
            @click="useSuggestion(suggestion)"
            class="suggestion-chip stk-block stk-w-full stk-mb-2 stk-px-2 stk-py-2 stk-rounded-lg stk-text-left"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Chat Messages -->
      <div v-if="messages.length > 0" class="chat-messages stk-flex-1 stk-p-4 stk-overflow-y-auto">
        <!-- Message History -->
        <div v-for="(message, index) in messages" :key="index" class="stk-mb-1">
          <div
            :class="[
              'message-bubble stk-rounded-lg stk-max-w-xs',
              message.type === 'user'
                ? 'message-bubble-user stk-ml-auto'
                : 'message-bubble-bot'
            ]"
          >
            <div v-if="message.isLoading" class="stk-flex stk-items-center">
              <i class="fas fa-spinner fa-spin stk-mr-2"></i>
              Analyzing...
            </div>
            <div v-else class="stk-whitespace-pre-wrap">{{ message.content }}</div>
          </div>
          <div class="stk-text-xxs stk-text-gray-500 stk-mt-0_5" :class="message.type === 'user' ? 'stk-text-right' : ''">
            {{ message.timestamp.toLocaleTimeString() }}
          </div>
        </div>
      </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input">
        <div class="stk-flex stk-gap-2 stk-items-end">
          <textarea
            v-model="userMessage"
            @keyup.enter.exact="sendMessage"
            @keyup.enter.shift.prevent="addNewLine"
            placeholder="Ask me anything about this client... (Shift+Enter for new line)"
            class="chat-textarea stk-flex-1"
            rows="2"
            ref="chatTextarea"
          ></textarea>
          <button
            @click="sendMessage"
            :disabled="!userMessage.trim()"
            class="stk-button is-primary send-button"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EntityChat',
  props: {
    entity: {
      type: Object,
      required: true,
    },
    currentRoute: {
      type: String,
      required: true,
    },
    clientRiskScore: {
      type: Number,
      default: null,
    },
    portfolioRiskScore: {
      type: Number,
      default: null,
    },
    clientValues: {
      type: Array,
      default: () => []
    },
    hasPortfolioData: {
      type: Boolean,
      default: false
    },
    isFetchingPortfolioRisk: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showChat: false,
      userMessage: '',
      messages: [],
      conversationId: null, // Track current conversation
      analysisDepth: 'standard', // or 'comprehensive'
      availableDocuments: [], // Fetched from API
      selectedDocumentIds: [], // User selections
      loadingDocuments: false,
      enabledSources: [
        { id: 'portfolio', label: 'Portfolio', enabled: true },
        { id: 'dossier', label: 'Dossier', enabled: true },
        { id: 'meetings', label: 'Meetings', enabled: true },
        { id: 'recommendations', label: 'Recommendations', enabled: true },
      ]
    };
  },
  computed: {
    // Get meeting from entity data
    currentMeeting() {
      return this.entity?.latest_meeting || null;
    },

    // Check if we have meeting content
    hasMeetingContent() {
      return this.currentMeeting && (this.currentMeeting.summary || this.currentMeeting.transcript);
    },

    // Check for risk mismatch
    hasRiskMismatch() {
      return this.portfolioRiskScore !== null &&
             this.clientRiskScore !== null &&
             Math.abs(this.portfolioRiskScore - this.clientRiskScore) > 10;
    },

    // Meeting-aware suggestions
    chatSuggestions() {
      const suggestions = [];

      // Meeting-specific suggestions if we have meeting data
      if (this.currentMeeting) {
        if (this.currentMeeting.summary) {
          suggestions.push("Summarize what we discussed in this meeting");
          suggestions.push("What were the key decisions made?");
        }

        if (this.currentMeeting.transcript) {
          suggestions.push("What questions did the client ask?");
          suggestions.push("What follow-up actions do we need?");
        }

        if (this.currentMeeting.crm_tasks && this.currentMeeting.crm_tasks.length > 0) {
          suggestions.push("What are the outstanding action items?");
        }

        // Tab-specific meeting suggestions
        if (this.currentRoute === 'entity-note-taking') {
          suggestions.push("What should I focus on in our next meeting?");
          suggestions.push("Were there any concerns raised?");
        }
      } else {
        // Fallback to general suggestions when no meeting
        suggestions.push("What should I prepare for our next meeting?");
        suggestions.push("Summarize this client's situation");
      }

      // Add tab-specific suggestions
      if (this.currentRoute === 'entity-portfolio') {
        if (this.hasRiskMismatch) {
          suggestions.push("Why is there a risk mismatch?");
        }
      } else if (this.currentRoute === 'entity-dossier') {
        suggestions.push("What are the key action items?");
      }
      return suggestions.slice(0, 4); // Limit to 4 suggestions
    },

    // Get enabled sources for context
    enabledSourcesText() {
      const sources = this.enabledSources.filter(s => s.enabled).map(s => s.label.toLowerCase());
      if (this.selectedDocumentIds.length > 0) {
        sources.push(`${this.selectedDocumentIds.length} document(s)`);
      }
      return sources.join(', ');
    },
  },
  async mounted() {
    await this.fetchAvailableDocuments();
  },
  methods: {
    riskMapping(score) {
      const mappings = {
        '0-20':   { level: 'Conservative',            description: 'You prefer to keep your investments safe, even if it means potentially lower returns.' },
        '21-40':  { level: 'Moderately Conservative', description: 'You are willing to take on a little risk for some potential reward.' },
        '41-60':  { level: 'Moderate',                 description: 'You are comfortable with a moderate level of risk for moderate potential returns.' },
        '61-80':  { level: 'Moderately Aggressive',    description: 'You are willing to take on significant risk for higher potential returns.' },
        '81-100': { level: 'Aggressive',               description: 'You are willing to take on more risk for a potential higher reward, and you\'re okay with the volatility that comes with it.' },
      };

      for (const range in mappings) {
        const [min, max] = range.split('-').map(Number);
        if (score >= min && score <= max) {
          return mappings[range];
        }
      }

      return {
        level: 'Unknown',
        description: 'Risk score is not within expected range.'
      };
    },

    toggleChat() {
      this.showChat = !this.showChat;

      // Reset conversation when opening chat
      // This ensures fresh context with current data
      if (this.showChat) {
        this.conversationId = null;
        this.messages = [];
      }
    },

    async sendMessage() {
      if (!this.userMessage.trim()) return;

      const message = this.userMessage.trim();
      this.userMessage = '';

      // Add user message
      this.messages.push({
        type: 'user',
        content: message,
        timestamp: new Date()
      });

      // Add loading message
      this.messages.push({
        type: 'assistant',
        content: 'Analyzing...',
        timestamp: new Date(),
        isLoading: true
      });

      try {
        // Call real API
        const response = await fetch(`/api/v1/entities/${this.entity.external_id}/chat/`, {
          method: 'POST',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: message,
            conversation_id: this.conversationId, // Include current conversation ID
            conversation_history: this.messages.slice(0, -1).map(msg => ({
              type: msg.type,
              content: msg.content
            })),
            enabled_sources: this.enabledSources.filter(s => s.enabled).map(s => s.id),
            selected_document_ids: this.selectedDocumentIds,
            analysis_depth: this.analysisDepth,
            current_route: this.currentRoute
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Store conversation ID for future messages
        if (data.conversation_id) {
          this.conversationId = data.conversation_id;
        }

        // Remove loading message
        this.messages.pop();

        // Add actual response
        this.messages.push({
          type: 'assistant',
          content: data.response,
          timestamp: new Date()
        });

      } catch (error) {
        console.error('Chat API error:', error);

        // Remove loading message
        this.messages.pop();

        // Add error message
        this.messages.push({
          type: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again.",
          timestamp: new Date()
        });
      }
    },

    useSuggestion(suggestion) {
      this.userMessage = suggestion;
      this.sendMessage();
    },

    addNewLine() {
      // Allow Shift+Enter to add new lines
      this.userMessage += '\n';
      this.$nextTick(() => {
        this.$refs.chatTextarea.focus();
      });
    },
    async fetchAvailableDocuments() {
      this.loadingDocuments = true;
      try {
        const response = await fetch(`/api/v1/advisor-documents/${this.entity.external_id}`, {
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
          }
        });
        const data = await response.json();
        this.availableDocuments = data.documents || [];
      } catch (error) {
        console.error('Error fetching documents:', error);
        this.availableDocuments = [];
      } finally {
        this.loadingDocuments = false;
      }
    },

    truncateFilename(filename, maxLength = 25) {
      if (filename.length <= maxLength) return filename;
      const ext = filename.split('.').pop();
      const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
      const truncated = nameWithoutExt.substring(0, maxLength - ext.length - 4) + '...';
      return `${truncated}.${ext}`;
    },

    selectAllDocuments() {
      this.selectedDocumentIds = this.availableDocuments.map(doc => doc.external_id);
    },

    selectNoDocuments() {
      this.selectedDocumentIds = [];
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatBody = this.$refs.chatBody;
        chatBody.scrollTop = chatBody.scrollHeight;
      });
    },
  },
  watch: {
    messages() {
      this.scrollToBottom();
    }
  }
};
</script>

<style scoped>
.floating-chat-button {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 1000;
}

.chat-button {
  background-color: var(--stk-primary-95);
  border-radius: 9999px;
  border: 2px solid var(--stk-primary-60);
  color: var(--stk-primary-30);
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.chat-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.chat-button.active {
  background-color: var(--stk-primary-50);
  color: white;
  border-color: var(--stk-primary-30);
}

.chat-panel {
  position: fixed;
  bottom: 5rem;
  right: 1rem;
  width: 400px;
  height: 100%;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 6rem);
  overflow: hidden;
}

.chat-input {
  flex-grow: 0;
  flex-shrink: 0;
  background: #f8f9fa;
  z-index: 10;
  padding: 0.5rem;
  border-top: 1px solid var(--stk-border);
  border-radius: 0 0 0.5rem 0.5rem;
}

.chat-messages {
  flex: 1;
}

.chat-header {
  background-color: var(--stk-primary-40);
  color: var(--stk-primary-70);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  flex-grow: 0;
  flex-shrink: 0;
}

.chat-header-title {
  color: white;
  font-size: 1.125em;
  font-weight: 600;
  padding-left: 0.25em;
}

.close-chat-btn {
  width: 2.25em;
}

.chat-body {
  flex-grow: 1;
  overflow-y: auto;
}

.chat-controls {
  background: #f8f9fa;
}

.suggestion-chip {
  transition: all 0.2s ease;
  cursor: pointer;
  text-align: left;
  border: none;
  background-color: var(--stk-primary-90);
  color: var(--stk-primary-40);
}

.suggestion-chip:hover {
  background-color: var(--stk-primary-85) !important;
  transform: translateY(-1px);
  color: var(--stk-primary-30);
}

/* Utility classes */
.stk-block { display: block; }

.message-bubble {
  word-wrap: break-word;
  padding: 0.75em 0.875em;
  line-height: 1.25;
  font-size: 0.875em;
}

.message-bubble-user {
  background-color: var(--stk-primary-50);
  color: white;
}

.message-bubble-bot {
  background-color: var(--stk-text-95);
  color: var(--stk-text-30);
}

.chat-textarea {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  resize: none;
  min-height: 80px;
  max-height: 120px;
  overflow-y: auto;
  transition: border-color 0.2s ease;
}

.chat-textarea:focus {
  outline: none;
  border-color: var(--stk-primary-50);
  box-shadow: 0 0 0 3px var(--stk-primary-90);
}

.chat-textarea::placeholder {
  color: #9ca3af;
}

.send-button {
  width: 2.5em;
}

.stk-items-end { align-items: flex-end; }

/* Additional utility classes */
.stk-text-xs { font-size: 0.75rem; }
.stk-text-xxs { font-size: 0.625rem; }
.stk-gap-3 { gap: 0.75rem; }
.stk-mr-1 { margin-right: 0.25rem; }
.stk-mb-2 { margin-bottom: 0.5rem; }
.stk-mb-3 { margin-bottom: 0.75rem; }
.stk-mt-1 { margin-top: 0.25rem; }
.stk-mt-0_5 { margin-top: 0.125rem; }
.stk-p-3 { padding: 0.75rem; }
.stk-px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.stk-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.stk-text-gray-600 { color: #4b5563; }
.stk-flex-1 { flex: 1; }
.stk-flex { display: flex; }
.stk-items-center { align-items: center; }
.stk-justify-between { justify-content: space-between; }
.stk-gap-2 { gap: 0.5rem; }
.stk-mb-2 { margin-bottom: 0.5rem; }
.stk-mb-3 { margin-bottom: 0.75rem; }
.stk-mb-4 { margin-bottom: 1rem; }
.stk-mr-2 { margin-right: 0.5rem; }
.stk-ml-2 { margin-left: 0.5rem; }
.stk-ml-auto { margin-left: auto; }
.stk-mt-1 { margin-top: 0.25rem; }
.stk-mt-2 { margin-top: 0.5rem; }
.stk-p-3 { padding: 0.75rem; }
.stk-p-4 { padding: 1rem; }
.stk-px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.stk-py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.stk-text-sm { font-size: 0.875rem; }
.stk-text-lg { font-size: 1.125rem; }
.stk-font-medium { font-weight: 500; }
.stk-font-semibold { font-weight: 600; }
.stk-text-right { text-align: right; }
.stk-text-gray-500 { color: #6b7280; }
.stk-text-gray-600 { color: #4b5563; }
.stk-text-gray-900 { color: #111827; }
.stk-text-white { color: white; }
.stk-text-blue-600 { color: var(--stk-primary-50); }
.stk-text-blue-800 { color: var(--stk-primary-30); }
.stk-bg-gray-100 { background-color: #f3f4f6; }
.stk-bg-blue-100 { background-color: var(--stk-primary-90); }
.stk-bg-blue-500 { background-color: var(--stk-primary-50); }
.stk-border-b { border-bottom: 1px solid #e5e7eb; }
.stk-border-t { border-top: 1px solid #e5e7eb; }
.stk-rounded-lg { border-radius: 0.5rem; }
.stk-rounded-full { border-radius: 9999px; }
.stk-max-w-xs { max-width: 20rem; }
.stk-w-full { width: 100%; }
.stk-overflow-y-auto { overflow-y: auto; }
.stk-whitespace-pre-wrap { white-space: pre-wrap; }
.stk-space-y-1 > * + * { margin-top: 0.25rem; }
.hover\:stk-bg-blue-200:hover { background-color: #bfdbfe; }
.stk-max-h-24 { max-height: 6rem; }
.stk-overflow-y-auto { overflow-y: auto; }
.stk-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stk-mt-3 { margin-top: 0.75rem; }
</style>
